import type { PatientModel } from '@ctw/shared/api/fhir/models/patient';
import {
  getFirstPartyPatientByIdentifier,
  getPatientByID,
} from '@ctw/shared/api/fhir/patient-helper';
import { SYSTEM_ZUS_UNIVERSAL_ID } from '@ctw/shared/api/fhir/system-urls';
import { ContentError } from '@ctw/shared/components/errors/content-error';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import type { PatientFormData } from '@ctw/shared/content/forms/actions/patients';
import { SupportFormLink } from '@ctw/shared/content/support-form-link';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { DrawerProvider } from '@ctw/shared/context/drawer-context';
import { useFeatureFlag } from '@ctw/shared/context/feature-flag-provider';
import { ModalProvider } from '@ctw/shared/context/modal-context';
import { ResourceDrawerProvider } from '@ctw/shared/context/resource-drawer-context';
import { ResourceModalProvider } from '@ctw/shared/context/resource-modal-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import {
  type CustomQueryFnContext,
  type UseCtwQueryFnContext,
  type UseCtwQueryOptions,
  useCtwQuery,
} from '@ctw/shared/hooks/use-ctw-query';
import { useTriggerBackfillRequest } from '@ctw/shared/hooks/use-trigger-backfill-request';
import { useTriggerLensOnDemandJob } from '@ctw/shared/hooks/use-trigger-lens-on-demand-job';
import { QUERY_KEY_PATIENT } from '@ctw/shared/utils/query-keys';
import { retryWithExponentialBackoff } from '@ctw/shared/utils/request';
import { tw } from '@ctw/shared/utils/tailwind';
import { faUserLock } from '@fortawesome/pro-solid-svg-icons';
import { type UseQueryResult, useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  type PropsWithChildren,
  type ReactNode,
  useContext,
  useLayoutEffect,
  useMemo,
} from 'react';
import type { SetOptional } from 'type-fest';

export type ThirdPartyID = {
  patientResourceID?: never;
  patientUPID?: never;
  patientID: string;
  systemURL: string;
};

export type PatientUPIDSpecified = {
  patientResourceID?: never;
  patientUPID: string;
  patientID?: never;
  systemURL?: never;
};

export type PatientResourceID = {
  patientResourceID: string;
  patientUPID?: never;
  patientID?: never;
  systemURL?: never;
};

export interface PatientState {
  patientResourceID?: string;
  patientID?: string;
  systemURL?: string;
  patient: PatientModel;
  onPatientSave?: (data: PatientFormData) => void;
}

type PatientStateWithOptionalPatient = SetOptional<PatientState, 'patient'>;

const PatientContext = createContext<PatientState | undefined>(undefined);

type PatientProviderProps = {
  onPatientSave?: (data: PatientFormData) => void;
  /** Replaces the default "Patient not found" error. */
  notFoundFallback?: ReactNode;
} & PropsWithChildren &
  (ThirdPartyID | PatientUPIDSpecified | PatientResourceID);

export const PatientProvider = ({
  children,
  patientResourceID,
  patientUPID,
  patientID,
  systemURL,
  onPatientSave,
  notFoundFallback,
}: PatientProviderProps) => {
  const patient = useFetchPatient({
    patientUPID,
    patientID,
    patientResourceID,
    systemURL,
  });
  const { requestContext } = useCTW();
  const telemetry = useTelemetry();
  const queryClient = useQueryClient();

  useTriggerBackfillRequest(systemURL, patientID);
  useTriggerLensOnDemandJob('encounters', patient.data?.UPID);
  useTriggerLensOnDemandJob('medications', patient.data?.UPID);
  useTriggerLensOnDemandJob('conditions', patient.data?.UPID);

  const providerState: PatientStateWithOptionalPatient = useMemo(
    () => ({
      patientResourceID,
      patientID: patientUPID ?? patientID,
      systemURL: patientUPID ? SYSTEM_ZUS_UNIVERSAL_ID : systemURL,
      patient: patient.data,
      onPatientSave,
    }),
    [patient, patientResourceID, patientID, patientUPID, systemURL, onPatientSave],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: No explanation was provided. Please be a good citizen and add one!
  useLayoutEffect(() => {
    telemetry.setPatient({
      patientUPID: patientUPID ?? patient.data?.UPID,
      patientID,
      patientResourceID,
      systemURL,
    });
  }, [patient, patientID, patientUPID, patientResourceID, systemURL, telemetry, queryClient]);

  if (isPatientStateWithPatient(providerState)) {
    if (providerState.patient.active !== true) {
      // Assume null is inactive (different from the FHIR spec) since we use this property to control CPR access.
      return (
        <ContentError
          title="Inactive patient"
          message="Follow your organization's guidelines to activate this patient in Zus."
          className={tw`flex h-full min-h-full w-full min-w-full items-center justify-center`}
        >
          <span>If this problem persists </span>
          <SupportFormLink buttonText="contact support" className={tw`text-base`} />.
        </ContentError>
      );
    }

    return (
      <PatientContext.Provider value={providerState}>
        <ModalProvider>
          {({ modal }) => (
            <ResourceModalProvider>
              <DrawerProvider allowDocking={false}>
                {({ drawer }) => (
                  <ResourceDrawerProvider>
                    {drawer}
                    {children}
                  </ResourceDrawerProvider>
                )}
              </DrawerProvider>
              {modal}
            </ResourceModalProvider>
          )}
        </ModalProvider>
      </PatientContext.Provider>
    );
  }

  if (patient.isLoading) {
    return (
      <LoadingSpinner
        centered={true}
        message={
          requestContext.service === 'standalone'
            ? 'Loading...'
            : 'Loading patient. This may take a minute if patient is being synced from EHR for the first time.'
        }
      />
    );
  }

  const notAuthorized =
    patient.error &&
    typeof patient.error === 'object' &&
    (('statusCode' in patient.error && patient.error.statusCode === 401) ||
      ('status' in patient.error && patient.error.status === 401));

  if (notAuthorized) {
    telemetry.trackError({
      message: 'User not authorized to view patient',
      error: patient.error,
      context: { patientID, patientResourceID, systemURL },
    });

    return (
      <ContentError
        icon={faUserLock}
        title="Not authorized"
        message="Your account does not have access to this feature."
        className={tw`flex h-full min-h-full w-full min-w-full items-center justify-center`}
      >
        <span>If this problem persists </span>
        <SupportFormLink buttonText="contact support" className={tw`text-base`} />.
      </ContentError>
    );
  }

  telemetry.trackError({
    message: 'User tried to view a patient that cannot be found',
    error: patient?.error ?? 'No patient found',
    context: { patientID, patientResourceID, systemURL },
  });

  if (notFoundFallback) {
    return notFoundFallback;
  }

  return (
    <ContentError
      title="Patient not found"
      message="Try reloading the app."
      className={tw`flex h-full min-h-full w-full min-w-full items-center justify-center`}
    >
      <span>If this problem persists </span>
      <SupportFormLink buttonText="contact support" className={tw`text-base`} />.
    </ContentError>
  );
};

interface ScopedPatientProviderProps extends PropsWithChildren {
  patientUPID: string;
}

export const ScopedPatientProvider = ({ children, patientUPID }: ScopedPatientProviderProps) => {
  const patient = useFetchPatient({
    patientUPID,
  });

  const providerState: PatientStateWithOptionalPatient = useMemo(
    () => ({
      patientID: patientUPID,
      systemURL: SYSTEM_ZUS_UNIVERSAL_ID,
      patient: patient.data,
    }),
    [patient, patientUPID],
  );

  if (isPatientStateWithPatient(providerState)) {
    if (providerState.patient.active !== true) {
      // Assume null is inactive (different from the FHIR spec) since we use this property to control CPR access.
      return (
        <ContentError
          title="Inactive patient"
          message="Follow your organization's guidelines to activate this patient in Zus."
          className={tw`flex h-full min-h-full w-full min-w-full items-center justify-center`}
        >
          <span>If this problem persists </span>
          <SupportFormLink buttonText="contact support" className={tw`text-base`} />.
        </ContentError>
      );
    }

    // `ScopedPatientProvider` only re-establishes patient context for portaled
    // / detached subtrees (modal bodies, sibling drawer-view bodies). It does
    // NOT create new `ResourceModal` / `ResourceDrawer` providers — those would
    // duplicate the outer providers and cause double-open on URL deep linking.
    // `useResourceModal` / `useResourceDrawer` calls within this subtree
    // resolve to the next outer provider.
    return <PatientContext.Provider value={providerState}>{children}</PatientContext.Provider>;
  }

  return <LoadingSpinner centered={true} message="Loading patient..." />;
};

// Wraps a subtree in `ScopedPatientProvider` so any descendant — including
// detached subtrees like portaled modal bodies or sibling drawer view bodies
// rendered inside a NavigationStack — can call `usePatientContext` /
// `usePatientQuery` against the resource's patient. Skips wrapping when the
// patient has no UPID so we don't fire a provider whose patient never
// resolves.
export const withPatientContext = (body: ReactNode, patientUPID: string | undefined) =>
  patientUPID ? (
    <ScopedPatientProvider patientUPID={patientUPID}>{body}</ScopedPatientProvider>
  ) : (
    body
  );

export function usePatientContext() {
  const context = useContext(PatientContext);
  if (!context) {
    throw new Error(
      'usePatientContext must be used within a PatientProvider or ScopedPatientProvider',
    );
  }

  return context;
}

export const usePatientContextIfAvailable = () => useContext(PatientContext);

interface MockPatientProviderProps extends PropsWithChildren {
  patient: PatientModel;
}

/**
 * Places a patient directly into `PatientContext` without firing any FQS / Smile queries. Use
 * this when a component (e.g. `DocumentViewer`) calls `usePatientContextIfAvailable()` to
 * short-circuit its own patient fetch — providing this provider satisfies the gate without a
 * working `CTWProvider` request pipeline. This covers Storybook / test mounts and internal
 * tooling that renders a document with no real patient behind it.
 *
 * App code rendering an actual patient's data should use `PatientProvider` (which fetches the
 * patient by id/UPID); this mock has no resource drawer / modal nesting because storybook
 * decorators set those up separately.
 */
export const MockPatientProvider = ({ patient, children }: MockPatientProviderProps) => {
  const value: PatientState = useMemo(
    () => ({
      patient,
      patientID: patient.UPID,
      systemURL: SYSTEM_ZUS_UNIVERSAL_ID,
    }),
    [patient],
  );
  return <PatientContext.Provider value={value}>{children}</PatientContext.Provider>;
};

function isPatientStateWithPatient(state: PatientStateWithOptionalPatient): state is PatientState {
  return Boolean(state.patient);
}

interface UseFetchPatientOptions {
  patientUPID?: string;
  patientID?: string;
  patientResourceID?: string;
  systemURL?: string;
}

const useFetchPatient = ({
  patientUPID,
  patientID,
  patientResourceID,
  systemURL,
}: UseFetchPatientOptions): UseQueryResult<PatientModel> => {
  const crossBuilderLookup = useFeatureFlag('ctw-cross-builder-upid-lookup');
  return useCtwQuery({
    queryId: QUERY_KEY_PATIENT,
    queryKey: [patientUPID, patientID, patientResourceID, systemURL],
    // Cache patient resource for longer than other queries to prevent redundant re-fetches on
    // every navigation.
    staleTime: /* 20 minutes */ 1000 * 60 * 20,
    gcTime: /* 20 minutes */ 1000 * 60 * 20,
    queryFn: async ({ requestContext, ctwFetch }) => {
      if (patientResourceID) {
        return await getPatientByID(ctwFetch, requestContext, patientResourceID);
      }

      if (patientUPID) {
        return await getFirstPartyPatientByIdentifier(
          ctwFetch,
          requestContext,
          patientUPID,
          SYSTEM_ZUS_UNIVERSAL_ID,
          crossBuilderLookup,
        );
      }

      if (patientID && systemURL) {
        return await retryWithExponentialBackoff(
          () => {
            return getFirstPartyPatientByIdentifier(
              ctwFetch,
              requestContext,
              patientID,
              systemURL,
              crossBuilderLookup,
            );
          },
          6,
          2000,
          120000,
        );
      }

      throw new Error('No patient identifier provided');
    },
  });
};

export interface UsePatientQueryFnContext<CustomContext extends CustomQueryFnContext>
  extends UseCtwQueryFnContext<CustomContext> {
  patient: PatientModel;
}

export interface UsePatientQueryOptions<TData, TContext extends CustomQueryFnContext>
  extends Omit<UseCtwQueryOptions<TData, TContext>, 'queryFn'> {
  queryFn: (context: UsePatientQueryFnContext<TContext>) => Promise<TData>;
}

export function usePatientQuery<
  TData,
  TContext extends CustomQueryFnContext = CustomQueryFnContext,
>(options: UsePatientQueryOptions<TData, TContext>) {
  const { patient, patientID, patientResourceID, systemURL } = usePatientContext();
  const patientQueryMeta = useMemo(
    () => ({
      ...(options.queryMeta ?? {}),
      patientID,
      patientResourceID,
      systemURL,
    }),
    [options.queryMeta, patientID, patientResourceID, systemURL],
  );

  const useQueryOptionsWithContextualQueryKeys: UseCtwQueryOptions<TData, TContext> = useMemo(
    () => ({
      ...options,
      queryMeta: patientQueryMeta,
      // Prefix the query ID with `patient.`
      queryId: `patient.${options.queryId}`,
      // Add the patient's UPID to the query key so we never cache across patients
      queryKey: [...(options.queryKey ?? []), patient.resource.id],
      // Add the patient into the query function context
      queryFn: (context: UseCtwQueryFnContext<TContext>) => {
        context.setScopedContextProperty('patientQueryMeta', patientQueryMeta);

        return options.queryFn({
          ...(options.queryFnContext ?? {}),
          ...context,
          patient,
        });
      },
    }),
    [options, patient, patientQueryMeta],
  );

  return useCtwQuery(useQueryOptionsWithContextualQueryKeys);
}
